import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { inviteApi } from './inviteApi';

const POLL_INTERVAL_MS = 4000;
const SearchProgressContext = createContext(null);

/**
 * One running-search source of truth shared by Reach Out, Search Results and
 * any other tab. The provider polls the active search every 4s while it is
 * `running`, surfaces it to subscribers, and exposes `start` / `clear` /
 * `cancel` actions.
 */
export function SearchProgressProvider({ children }) {
  const [activeSearchId, setActiveSearchId] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [bumpCounter, setBumpCounter] = useState(0);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchOnce = useCallback(async (id) => {
    if (!id) return null;
    try {
      const data = await inviteApi.getSearch(id);
      return data;
    } catch (err) {
      return { error: err.response?.data?.detail || err.message || 'Polling failed' };
    }
  }, []);

  const tick = useCallback(async (id) => {
    if (!id || cancelledRef.current) return;
    const fresh = await fetchOnce(id);
    if (cancelledRef.current) return;
    if (!fresh) return;
    if (fresh.error) {
      setSnapshot((prev) => ({ ...(prev || {}), error: fresh.error }));
      timerRef.current = setTimeout(() => tick(id), POLL_INTERVAL_MS * 2);
      return;
    }
    setSnapshot(fresh);
    setBumpCounter((c) => c + 1);
    // Continue polling while EITHER the search itself is running OR a
    // background qualification job is in progress.
    const stillSearching = fresh.status === 'running';
    const stillQualifying = fresh.qualification_status === 'qualifying';
    if (stillSearching || stillQualifying) {
      timerRef.current = setTimeout(() => tick(id), POLL_INTERVAL_MS);
    }
  }, [fetchOnce]);

  const start = useCallback((id) => {
    stopPolling();
    cancelledRef.current = false;
    setActiveSearchId(id);
    setSnapshot(null);
    tick(id);
  }, [stopPolling, tick]);

  const clear = useCallback(() => {
    stopPolling();
    setActiveSearchId(null);
    setSnapshot(null);
  }, [stopPolling]);

  const cancel = useCallback(async () => {
    if (!activeSearchId) return;
    try {
      await inviteApi.cancelSearch(activeSearchId);
      const fresh = await fetchOnce(activeSearchId);
      if (fresh && !fresh.error) setSnapshot(fresh);
    } catch (err) {
      // Surface error in snapshot
      setSnapshot((prev) => ({ ...(prev || {}), error: err.response?.data?.detail || 'Cancel failed' }));
    }
  }, [activeSearchId, fetchOnce]);

  useEffect(() => () => {
    cancelledRef.current = true;
    stopPolling();
  }, [stopPolling]);

  // Allow consumers to trigger a manual one-shot refresh (e.g. after retry).
  const refresh = useCallback(async () => {
    if (!activeSearchId) return null;
    const fresh = await fetchOnce(activeSearchId);
    if (fresh && !fresh.error) setSnapshot(fresh);
    return fresh;
  }, [activeSearchId, fetchOnce]);

  const value = {
    activeSearchId,
    snapshot,
    start,
    clear,
    cancel,
    refresh,
    bumpCounter,
  };

  return (
    <SearchProgressContext.Provider value={value}>
      {children}
    </SearchProgressContext.Provider>
  );
}

export function useSearchProgressShared() {
  const ctx = useContext(SearchProgressContext);
  if (!ctx) {
    // Safe no-op fallback for any consumer rendered outside the provider.
    return {
      activeSearchId: null,
      snapshot: null,
      start: () => {},
      clear: () => {},
      cancel: () => {},
      refresh: async () => null,
      bumpCounter: 0,
    };
  }
  return ctx;
}
